import React, { useEffect, useMemo, useState } from 'react';
import { sgsstAutoeval } from '../services/api';
import SgsstTabs from '../components/SgsstTabs';
import { S, useFlash, fdate } from '../components/sgsstUi';

const yNow = new Date().getFullYear();
const CLAS = {
  critico: { l: 'Crítico', c: '#ef4444' },
  moderadamente_aceptable: { l: 'Moderadamente aceptable', c: '#f59e0b' },
  aceptable: { l: 'Aceptable', c: '#10b981' },
};
const CUMPLE = [['cumple', 'Cumple'], ['no_cumple', 'No cumple'], ['no_aplica', 'No aplica']];
const clasificar = (p) => p >= 85 ? 'aceptable' : p >= 60 ? 'moderadamente_aceptable' : 'critico';

export default function SgsstAutoevaluacion() {
  const [lista, setLista] = useState([]);
  const [ev, setEv] = useState(null);       // evaluación abierta (con items)
  const [msgNode, flash] = useFlash();
  const [busy, setBusy] = useState(false);

  const load = () => sgsstAutoeval.list().then(r => setLista(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const abrir = async (id) => { try { const { data } = await sgsstAutoeval.get(id); setEv(data); } catch (e) { flash(false, 'No se pudo abrir'); } };
  const crear = async () => {
    setBusy(true);
    try { const { data } = await sgsstAutoeval.create({ anio: yNow }); await abrir(data.id); load(); }
    catch (e) { flash(false, 'No se pudo crear'); } finally { setBusy(false); }
  };
  const borrar = async (r) => { if (!window.confirm(`¿Eliminar la autoevaluación ${r.anio}?`)) return; try { await sgsstAutoeval.remove(r.id); load(); } catch (e) {} };

  if (ev) return <Detalle ev={ev} setEv={setEv} onSaved={load} flash={flash} msgNode={msgNode} />;

  return (
    <div style={S.wrap}>
      <SgsstTabs />
      <div style={S.head}>
        <div><h1 style={S.h1}>Autoevaluación de estándares mínimos</h1><p style={S.sub}>Resolución 0312/2019 — 60 estándares, puntaje sobre 100.</p></div>
        <button style={S.btn} disabled={busy} onClick={crear}>+ Nueva autoevaluación</button>
      </div>
      {msgNode}
      <div style={S.card}><div style={{ overflowX: 'auto' }}>
        <table style={S.table}>
          <thead><tr>{['Año', 'Fecha', 'Responsable', 'Puntaje', 'Clasificación', 'Estado', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={7} style={S.empty}>Sin autoevaluaciones. Crea la primera.</td></tr>}
            {lista.map(r => { const cl = CLAS[r.clasificacion] || CLAS.critico; return (
              <tr key={r.id}>
                <td style={{ ...S.td, color: '#e2e8f0', fontWeight: 700 }}>{r.anio}</td>
                <td style={S.td}>{fdate(r.fecha)}</td>
                <td style={S.td}>{r.responsable || '—'}</td>
                <td style={{ ...S.td, fontFamily: 'ui-monospace', fontWeight: 700 }}>{Number(r.puntaje).toFixed(1)}%</td>
                <td style={S.td}><span style={{ ...S.badge, background: cl.c + '22', color: cl.c }}>{cl.l}</span></td>
                <td style={S.td}>{r.estado}</td>
                <td style={S.td}><button style={S.link} onClick={() => abrir(r.id)}>Abrir</button><button style={{ ...S.link, color: '#fca5a5' }} onClick={() => borrar(r)}>Eliminar</button></td>
              </tr>); })}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

function Detalle({ ev, setEv, onSaved, flash, msgNode }) {
  const [items, setItems] = useState(ev.items);
  const [resp, setResp] = useState(ev.responsable || '');
  const [busy, setBusy] = useState(false);

  const puntaje = useMemo(() => +items.reduce((s, i) => s + ((i.cumple === 'cumple' || i.cumple === 'no_aplica') ? Number(i.valor) : 0), 0).toFixed(2), [items]);
  const clas = CLAS[clasificar(puntaje)];

  const setItem = (num, k, v) => setItems(items.map(i => i.num === num ? { ...i, [k]: v } : i));

  const guardar = async (cerrar) => {
    setBusy(true);
    try {
      const { data } = await sgsstAutoeval.update(ev.id, { items: items.map(i => ({ item_num: i.num, cumple: i.cumple, observacion: i.observacion })), responsable: resp, estado: cerrar ? 'cerrada' : 'abierta' });
      flash(true, cerrar ? '✅ Autoevaluación cerrada' : '✅ Guardado');
      onSaved();
      if (cerrar) setEv(null); else setEv({ ...ev, ...data, items });
    } catch (e) { flash(false, '❌ ' + (e.response?.data?.error || 'No se pudo')); } finally { setBusy(false); }
  };

  // Agrupar por ciclo → estándar
  const grupos = {};
  items.forEach(i => { grupos[i.ciclo] = grupos[i.ciclo] || {}; (grupos[i.ciclo][i.estandar] = grupos[i.ciclo][i.estandar] || []).push(i); });

  return (
    <div style={S.wrap}>
      <SgsstTabs />
      <button style={{ ...S.link, marginBottom: 10 }} onClick={() => setEv(null)}>← Volver a la lista</button>
      {msgNode}
      <div style={{ ...S.card, padding: 16, marginBottom: 14, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
          <div><div style={{ fontSize: 34, fontWeight: 800, fontFamily: 'ui-monospace', color: clas.c }}>{puntaje.toFixed(1)}%</div><div style={{ fontSize: 12, color: '#64748b' }}>Autoevaluación {ev.anio}</div></div>
          <span style={{ ...S.badge, background: clas.c + '22', color: clas.c, fontSize: 14, padding: '5px 14px' }}>{clas.l}</span>
          <div style={{ flex: 1, minWidth: 120, height: 8, background: '#0f172a', borderRadius: 6, overflow: 'hidden' }}><div style={{ width: puntaje + '%', height: '100%', background: clas.c }} /></div>
          <input placeholder="Responsable" style={{ ...S.in, maxWidth: 200 }} value={resp} onChange={e => setResp(e.target.value)} />
          <button style={S.btnGhost} disabled={busy} onClick={() => guardar(false)}>{busy ? '…' : 'Guardar'}</button>
          <button style={S.btn} disabled={busy} onClick={() => guardar(true)}>Cerrar evaluación</button>
        </div>
      </div>

      {Object.entries(grupos).map(([ciclo, ests]) => (
        <div key={ciclo} style={{ marginBottom: 16 }}>
          <div style={{ color: '#f59e0b', fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, margin: '6px 0 8px' }}>{ciclo}</div>
          {Object.entries(ests).map(([est, its]) => (
            <div key={est} style={{ ...S.card, marginBottom: 10 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #334155', color: '#e2e8f0', fontWeight: 700, fontSize: 13.5 }}>{est}</div>
              <div>
                {its.map(i => (
                  <div key={i.num} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid #1e293b' }}>
                    <span style={{ fontFamily: 'ui-monospace', color: '#64748b', fontSize: 12, minWidth: 42 }}>{i.num}</span>
                    <span style={{ flex: 1, minWidth: 200, color: '#cbd5e1', fontSize: 13 }}>{i.descripcion}</span>
                    <span style={{ fontFamily: 'ui-monospace', color: '#94a3b8', fontSize: 12, minWidth: 40, textAlign: 'right' }}>{Number(i.valor)}%</span>
                    <select style={{ ...S.in, width: 130 }} value={i.cumple} onChange={e => setItem(i.num, 'cumple', e.target.value)}>
                      {CUMPLE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
